import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ImageUpload from '../ui/ImageUpload';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  Home,
  Phone,
  User,
  Upload,
  Link
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLodges = () => {
  const [lodges, setLodges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [useUpload, setUseUpload] = useState(true);
  const [formData, setFormData] = useState({
    name_sl: '',
    name_en: '',
    description_sl: '',
    description_en: '',
    manager: '',
    phone: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    fetchLodges();
  }, []);

  const fetchLodges = async () => {
    try {
      const response = await axios.get(`${API}/lodges`);
      if (response.data && response.data.length > 0) {
        setLodges(response.data);
      } else {
        await seedLodges();
      }
    } catch (error) {
      console.error('Error fetching lodges:', error);
      await seedLodges();
    } finally {
      setLoading(false);
    }
  };

  const seedLodges = async () => {
    try {
      await axios.post(`${API}/seed-lodges`);
      const response = await axios.get(`${API}/lodges`);
      setLodges(response.data);
      toast.success('Koče so bile uvožene');
    } catch (error) {
      console.error('Error seeding lodges:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await axios.put(`${API}/lodges/${editingId}`, formData);
        toast.success('Koča je bila posodobljena');
      } else {
        const newOrder = lodges.length + 1;
        await axios.post(`${API}/lodges`, { ...formData, order: newOrder });
        toast.success('Nova koča je bila dodana');
      }
      
      resetForm();
      fetchLodges();
    } catch (error) {
      console.error('Error saving lodge:', error);
      toast.error('Napaka pri shranjevanju');
    }
  };

  const handleEdit = (lodge) => {
    setEditingId(lodge.id);
    setFormData({
      name_sl: lodge.name_sl,
      name_en: lodge.name_en,
      description_sl: lodge.description_sl,
      description_en: lodge.description_en,
      manager: lodge.manager,
      phone: lodge.phone,
      image: lodge.image || '',
      order: lodge.order
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to kočo?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/lodges/${id}`);
      toast.success('Koča je bila izbrisana');
      fetchLodges();
    } catch (error) {
      console.error('Error deleting lodge:', error);
      toast.error('Napaka pri brisanju');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({
      name_sl: '',
      name_en: '',
      description_sl: '',
      description_en: '',
      manager: '',
      phone: '',
      image: '',
      order: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Home className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Lovske koče</h2>
          <span className="px-2 py-1 text-xs bg-stone-800 text-stone-400 rounded-full">
            {lodges.length} koč
          </span>
        </div>
        {!showAddForm && (
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Dodaj kočo
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="bg-stone-800/50 border-stone-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Uredi kočo' : 'Dodaj novo kočo'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Ime (slovensko)</Label>
                <Input
                  value={formData.name_sl}
                  onChange={(e) => setFormData({ ...formData, name_sl: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="Lovska koča Pranger"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Ime (angleško)</Label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="Pranger Hunting Lodge"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Opis (slovensko)</Label>
              <Textarea
                value={formData.description_sl}
                onChange={(e) => setFormData({ ...formData, description_sl: e.target.value })}
                required
                rows={3}
                className="bg-stone-900/50 border-stone-700 text-white resize-none"
                placeholder="Opis koče v slovenščini..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Opis (angleško)</Label>
              <Textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                required
                rows={3}
                className="bg-stone-900/50 border-stone-700 text-white resize-none"
                placeholder="Lodge description in English..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Upravnik</Label>
                <Input
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="Janez Novak"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="041 123 456"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-stone-300">Slika koče</Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setUseUpload(true)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors flex items-center gap-1 ${
                      useUpload 
                        ? 'bg-emerald-600/20 text-emerald-400' 
                        : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                    }`}
                  >
                    <Upload className="w-3 h-3" />
                    Naloži
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUpload(false)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors flex items-center gap-1 ${
                      !useUpload 
                        ? 'bg-emerald-600/20 text-emerald-400' 
                        : 'bg-stone-700 text-stone-400 hover:bg-stone-600'
                    }`}
                  >
                    <Link className="w-3 h-3" />
                    URL
                  </button>
                </div>
              </div>
              
              {useUpload ? (
                <ImageUpload
                  value={formData.image}
                  onChange={(url) => setFormData({ ...formData, image: url })}
                  previewClassName="w-full h-40"
                />
              ) : (
                <div className="space-y-2">
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="bg-stone-900/50 border-stone-700 text-white"
                    placeholder="https://example.com/slika.jpg"
                  />
                  {formData.image && (
                    <div className="w-full h-40 rounded-lg overflow-hidden bg-stone-900">
                      <img 
                        src={formData.image} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-stone-500">
                Priporočena velikost: 800x500 px
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Shrani spremembe' : 'Dodaj kočo'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
              >
                <X className="w-4 h-4 mr-2" />
                Prekliči
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Lodges List */}
      <div className="grid gap-4">
        {lodges.map((lodge) => (
          <Card
            key={lodge.id}
            className="bg-stone-800/50 border-stone-700/50 overflow-hidden hover:border-emerald-600/30 transition-colors"
          >
            <div className="flex">
              {/* Image */}
              <div className="w-48 h-36 flex-shrink-0">
                <img
                  src={lodge.image}
                  alt={lodge.name_sl}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=800&h=500&fit=crop';
                  }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 p-4 flex flex-col justify-between">
                <div>
                  <h4 className="text-white font-semibold text-lg">{lodge.name_sl}</h4>
                  <p className="text-stone-400 text-sm line-clamp-2 mt-1">
                    {lodge.description_sl}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-stone-500">
                    <User className="w-4 h-4" />
                    {lodge.manager}
                  </span>
                  <span className="flex items-center gap-1 text-stone-500">
                    <Phone className="w-4 h-4" />
                    {lodge.phone}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 flex flex-col justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(lodge)}
                  className="border-stone-700 text-stone-300 hover:bg-stone-800"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(lodge.id)}
                  className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminLodges;
